import React, { useContext, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import Honeybadger from 'honeybadger-js';
import { Action } from '../Card';
import { Room } from '../../libs/gs-component-hotel-planner';
import HotelPlannerLogo from '../PageCard/HotelPlannerLogo';
import { getHotelIds } from '../../helpers/getHotelPlannerIds';
import { getHpLocation } from '../../helpers/getHpLocation';
import { useImageUploader } from '../../helpers/images/useImageUploader';
import addItemToPage from '../../helpers/apolloCache/addItemToPage';
import { mapRoomToPageItem } from '../../helpers/mapRoomToPageItem';
import styles from './styles.scss';
import CVAlert from '../CVAlert';
import HotelPlanner from '../../libs/gs-component-hotel-planner';
import { useMobileMedia } from '@xo-union/tk-ui-breakpoints';
import { Button } from '@xo-union/tk-component-buttons';

import {
  useWeddingWebsiteQuery,
  useEventsQuery,
  AccommodationItemsFragment,
  useCreateAccommodationItemMutation,
  PagesQuery,
} from '../../generated/graphql';
import Icon from '@xo-union/tk-component-icons';
import { TooltipContainer, Tooltip } from '@xo-union/tk-component-tooltips';
import classNames from 'classnames';
import { TrackingContext } from '../../TrackingContext';
import { useGetHotelEngineUrl } from '../../hooks/useGetHotelEngineUrl';

type Page = PagesQuery['pages'][0];
type AccommodationItem = AccommodationItemsFragment | undefined;

type TransientRoomRailCardProps = {
  page: Page;
  isRailCard?: boolean;
};

export const HotelPlannerTooltip = () => {
  const media = useMobileMedia();
  const [showTooltip, setShowTooltip] = useState(false);

  function setTooltip(): void {
    if (!media.yes) return;
    setShowTooltip(!showTooltip);
  }

  return (
    <TooltipContainer className={styles.hotelPlannerTooltip__container}>
      <span data-testid="hotel-planner-tip" className={styles.hotelPlannerTooltip__icon} onClick={setTooltip}>
        <Icon name="info" size="sm" />
      </span>
      <Tooltip
        data-testid="hotel-planner-tooltip"
        arrowPosition="top"
        arrowOffset={media.yes ? 73 : 50}
        className={classNames(styles.hotelPlannerTooltip__tooltip, {
          [styles.hotelPlannerTooltip__tooltip__show]: showTooltip,
        })}
      >
        Get custom quotes for hotel room blocks near your wedding.
      </Tooltip>
    </TooltipContainer>
  );
};

export const HotelPlannerCalloutRC = ({ isRailCard }: { isRailCard: boolean }) => {
  const { getHotelEngineUrl } = useGetHotelEngineUrl();
  const hotelEngineUrl = getHotelEngineUrl();

  const { track } = useContext(TrackingContext);

  const onClickLink = () => {
    track('Item Action Initiated', {
      action: 'hotel_added',
      actionName: 'hotel_added_link',
    });

    window.open(hotelEngineUrl);
  };

  return (
    <div
      data-testid="hotel-planner-callout"
      className={classNames(styles.hotelPlannerCallout, { [styles.hotelPlannerCallout__railCard]: isRailCard })}
    >
      <Action iconName="linkout" onClick={onClickLink} className={styles.hotelPlannerCallout__link} rel="sponsored">
        <span>Add Hotel Room Blocks</span>
      </Action>
      {!!isRailCard && <span>Give your out of town guests a great place to stay</span>}
      <div
        className={classNames(styles.hotelPlannerCallout__link__logoContainer, {
          [styles.hotelPlannerCallout__link__logoContainer__dashboard]: !isRailCard,
        })}
      >
        <HotelPlannerLogo copy={'powered by'} />
        <HotelPlannerTooltip />
      </div>
      {!!isRailCard && (
        <Button
          color="primary-alternative"
          onClick={onClickLink}
          rel="sponsored"
          className={styles.hotelPlannerCallout__link__button}
        >
          Book hotel room blocks
        </Button>
      )}
    </div>
  );
};

const TransientRoomRailCard: React.FC<TransientRoomRailCardProps> = ({ page, isRailCard = true }) => {
  const { data } = useWeddingWebsiteQuery();
  const hotelPlanerIds = useMemo(() => getHotelIds(page.items as AccommodationItemsFragment[]), [page.items]);
  const [createAccommodation] = useCreateAccommodationItemMutation();
  const { data: eventsData } = useEventsQuery();
  const [erroring, setErroring] = useState(false);

  const weddingLocation: string | undefined | null = useMemo(
    () => getHpLocation(eventsData?.events, data?.weddingWebsite?.weddingLocation),
    [data, eventsData]
  );
  const { imageUpload } = useImageUploader();

  const addTransientRoom = async (room: Room) => {
    try {
      const res = await imageUpload({ imageUrl: room.imagePath });
      const photo = res.data;

      await createAccommodation({
        variables: {
          attributes: mapRoomToPageItem(page.id, room, photo),
        },
        update(cache, { data }) {
          addItemToPage<AccommodationItem>(cache, page.id, data?.item);
        },
      });
    } catch (error) {
      Honeybadger.notify(error, {
        message: 'Error adding transient room',
        context: {
          error,
        },
      });
      setErroring(true);
    }
  };

  return (
    <>
      <HotelPlannerCalloutRC isRailCard={isRailCard} />
      {data?.wedding?.weddingDate && !isEmpty(weddingLocation) && (
        <>
          <CVAlert erroring={erroring} onTimeout={() => setErroring(false)} />
          <div
            className={classNames({
              [styles.transientRoomRailCard__hotelPlanner__pageCardWrapper]: !isRailCard,
            })}
          >
            <HotelPlanner
              weddingDate={data.wedding.weddingDate}
              weddingLocation={weddingLocation || ''}
              onClick={addTransientRoom}
              exclude={hotelPlanerIds}
              newVersion
              useCarousel={isRailCard}
            />
          </div>
        </>
      )}
    </>
  );
};

export default TransientRoomRailCard;
